import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon } from "../components/Icon.js";
import { Btn } from "../components/index.js";
import { ReviewGate } from "../components/ReviewGate.js";
import { useCloseTo } from "../lib/useCloseTo.js";
import { useSurface } from "../lib/useSurface.js";
import { useWakeLock } from "../lib/useWakeLock.js";
import { sendVideoOrQueue } from "../lib/offline.js";
import { rememberCaptureOrigin } from "../lib/captureOrigin.js";
import { mmss } from "../lib/demo.js";

type Phase = "idle" | "recording" | "paused" | "saving";

/** The finished take (live recording OR picked file) held for the confirmation gate. */
interface ReviewState {
  blob: Blob;
  url: string;
  /** accumulated ACTIVE recording time; 0 for a picked file (duration unknown up front) */
  durationMs: number;
  /** false once <video> reports it can't play the blob on this device */
  playable: boolean;
  /** upload filename (picked file keeps its own name) */
  filename: string;
}

/** Best-supported recording container/codecs, in preference order. */
function pickMime(): string | undefined {
  const types = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
  for (const t of types) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(t)) return t;
  }
  return undefined;
}

/**
 * Screen-recording capture (PC). Records the chosen screen / window / tab via getDisplayMedia,
 * mixes the microphone (and optional system audio) with the Web Audio API, and uploads the result
 * as a single `video` manual — the SAME async AI pipeline as camera video, so no backend change is
 * needed. Desktop browsers only (getDisplayMedia is unavailable on most mobile browsers).
 */
export function CaptureScreen() {
  useSurface("field");
  const navigate = useNavigate();
  const closeTo = useCloseTo();
  const previewRef = useRef<HTMLVideoElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const displayRef = useRef<MediaStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const acRef = useRef<AudioContext | null>(null);

  // Active-time accounting (pause excluded): accumRef = confirmed active ms, segmentStartRef = current segment start.
  const accumRef = useRef(0);
  const segmentStartRef = useRef(0);

  const [phase, setPhase] = useState<Phase>("idle");
  const [elapsed, setElapsed] = useState(0); // accumulated active ms (display)
  const [error, setError] = useState<string | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [sysOn, setSysOn] = useState(true);
  const [srMsg, setSrMsg] = useState(""); // one-shot screen-reader announcements (pause / resume)

  // Confirmation gate (この内容で送りますか？) — nothing is uploaded until 『送る』.
  const [review, setReview] = useState<ReviewState | null>(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<number | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  // Draft manual created by a failed send — reused by 「もう一度送る」 so retries don't pile up drafts.
  const draftManualIdRef = useRef<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const reviewUrlRef = useRef<string | null>(null);
  const posterUrlRef = useRef<string | null>(null);

  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;

  // elapsed timer — runs only while actively recording (pause time excluded)
  useEffect(() => {
    if (phase !== "recording") return;
    const id = setInterval(() => setElapsed(accumRef.current + (Date.now() - segmentStartRef.current)), 250);
    return () => clearInterval(id);
  }, [phase]);

  // release everything on unmount (tracks + any preview/poster object URLs)
  useEffect(
    () => () => {
      stopTracks();
      if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
      if (posterUrlRef.current) URL.revokeObjectURL(posterUrlRef.current);
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  function stopTracks() {
    displayRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current?.getTracks().forEach((t) => t.stop());
    displayRef.current = null;
    micRef.current = null;
    if (acRef.current && acRef.current.state !== "closed") void acRef.current.close();
    acRef.current = null;
  }

  async function start() {
    setError(null);
    if (!supported) {
      setError("お使いのブラウザ・端末は画面録画に対応していません。PC の Chrome / Edge などでお試しください。");
      return;
    }
    let display: MediaStream;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: sysOn });
    } catch {
      setError("画面の共有がキャンセルされました。「画面を選んで録画開始」からもう一度お試しください。");
      return;
    }
    const videoTrack = display.getVideoTracks()[0];
    if (!videoTrack) {
      display.getTracks().forEach((t) => t.stop());
      setError("画面の映像を取得できませんでした。");
      return;
    }
    displayRef.current = display;

    let mic: MediaStream | null = null;
    if (micOn) {
      try {
        mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        micRef.current = mic;
      } catch {
        /* mic denied — continue with screen (and system audio) only */
      }
    }

    // Mix mic + optional system audio into one track (Web Audio). Single source → use it directly.
    const audioTracks: MediaStreamTrack[] = [];
    if (mic) audioTracks.push(...mic.getAudioTracks());
    if (sysOn) audioTracks.push(...display.getAudioTracks());
    let mixedTrack: MediaStreamTrack | null = null;
    if (audioTracks.length === 1) {
      mixedTrack = audioTracks[0];
    } else if (audioTracks.length > 1) {
      const ac = new AudioContext();
      acRef.current = ac;
      const dest = ac.createMediaStreamDestination();
      for (const t of audioTracks) ac.createMediaStreamSource(new MediaStream([t])).connect(dest);
      mixedTrack = dest.stream.getAudioTracks()[0];
    }

    const finalStream = new MediaStream(mixedTrack ? [videoTrack, mixedTrack] : [videoTrack]);

    if (previewRef.current) {
      previewRef.current.srcObject = display;
      void previewRef.current.play().catch(() => {});
    }

    // The browser's own "共有を停止" ends the video track — finish the recording when that happens.
    videoTrack.addEventListener("ended", () => stop());

    chunksRef.current = [];
    try {
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(finalStream, { mimeType: mime }) : new MediaRecorder(finalStream);
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => handleStopped();
      rec.start(1000);
      recorderRef.current = rec;
      accumRef.current = 0;
      segmentStartRef.current = Date.now();
      setElapsed(0);
      setPhase("recording");
    } catch {
      setError("録画を開始できませんでした。別のブラウザでお試しください。");
      stopTracks();
    }
  }

  function pause() {
    const rec = recorderRef.current;
    if (!rec || rec.state !== "recording") return;
    let paused = false;
    try {
      rec.pause();
      paused = true;
    } catch {
      /* this device can't pause — stay recording so the timeline accounting stays honest */
    }
    if (!paused) return;
    accumRef.current += Date.now() - segmentStartRef.current;
    setElapsed(accumRef.current);
    setPhase("paused");
    setSrMsg("録画を一時停止しました");
  }

  function resume() {
    const rec = recorderRef.current;
    if (!rec || rec.state !== "paused") return;
    let resumed = false;
    try {
      rec.resume();
      resumed = true;
    } catch {
      /* this device can't resume — stay paused rather than count un-recorded idle time */
    }
    if (!resumed) return;
    segmentStartRef.current = Date.now();
    setPhase("recording");
    setSrMsg("録画を再開しました");
  }

  function stop() {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      // Read the RECORDER state (not React phase): the videoTrack "ended" listener captures a
      // stale closure, and the recorder may be paused when the user hits "共有を停止".
      if (rec.state === "recording") {
        accumRef.current += Date.now() - segmentStartRef.current;
        setElapsed(accumRef.current);
      }
      grabPoster(); // capture a live frame while the stream is up (gate fallback visual)
      setPhase("saving");
      rec.stop(); // → onstop → handleStopped
    }
  }

  /** Recorder finished — open the confirmation gate instead of uploading right away. */
  function handleStopped() {
    const type = chunksRef.current[0]?.type || pickMime() || "video/webm";
    const blob = new Blob(chunksRef.current, { type });
    chunksRef.current = [];
    stopTracks();
    if (blob.size === 0) {
      clearReviewUrls();
      setError("録画に失敗しました。もう一度お試しください。");
      setPhase("idle");
      setReview(null);
      return;
    }
    const ext = type.includes("mp4") ? "mp4" : "webm";
    openReview(blob, accumRef.current, `screen_${Date.now()}.${ext}`);
  }

  /**
   * Pick a screen recording captured by the device's OS recorder (iOS Control Center / Android
   * screen record) — the only path on mobile, where getDisplayMedia is unavailable. Goes through
   * the same confirmation gate, then the same `video` pipeline.
   */
  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    openReview(file, 0, file.name || `screen_${Date.now()}.mp4`);
  }

  /** Hold the finished take for the gate (nothing uploaded until 『送る』). */
  function openReview(blob: Blob, durationMs: number, filename: string) {
    const url = URL.createObjectURL(blob);
    if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
    reviewUrlRef.current = url;
    setPhase("idle");
    setReview({ blob, url, durationMs, playable: true, filename });
  }

  /** Decorative poster (last preview frame) so the gate isn't a black box if playback fails. */
  function grabPoster() {
    const v = previewRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return;
    try {
      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(v, 0, 0, c.width, c.height);
      c.toBlob(
        (b) => {
          if (!b) return;
          const url = URL.createObjectURL(b);
          if (posterUrlRef.current) URL.revokeObjectURL(posterUrlRef.current);
          posterUrlRef.current = url;
          setPosterUrl(url);
        },
        "image/jpeg",
        0.8,
      );
    } catch {
      /* not ready — gate falls back to text */
    }
  }

  function clearReviewUrls() {
    if (reviewUrlRef.current) {
      URL.revokeObjectURL(reviewUrlRef.current);
      reviewUrlRef.current = null;
    }
    if (posterUrlRef.current) {
      URL.revokeObjectURL(posterUrlRef.current);
      posterUrlRef.current = null;
    }
    setPosterUrl(null);
  }

  /** 『送る』 — only now run the init → PUT → complete handshake. */
  async function sendReview() {
    if (!review || sending) return;
    setSending(true);
    await uploadAndGo(review.blob, review.filename);
  }

  /** 『撮り直す』 — drop the take, back to the start card (or the OS-recorder guide on mobile). */
  function retake() {
    setSendError(null);
    draftManualIdRef.current = null;
    clearReviewUrls();
    setReview(null);
    accumRef.current = 0;
    setElapsed(0);
    setPhase("idle");
  }

  /** 『破棄』 — discard the take entirely and leave the capture screen. */
  function discard() {
    setSendError(null);
    draftManualIdRef.current = null;
    clearReviewUrls();
    setReview(null);
    navigate(closeTo);
  }

  /**
   * Upload a finished video blob (live recording OR picked file) as one `video` manual, falling
   * back to the offline queue. If both fail, stay on the gate with the take intact for a retry.
   */
  async function uploadAndGo(blob: Blob, fallbackName: string) {
    setSendError(null);
    setSendProgress(0);
    const result = await sendVideoOrQueue(blob, {
      filename: fallbackName,
      manualId: draftManualIdRef.current,
      onProgress: setSendProgress,
    });
    setSendProgress(null);
    if (result.status === "failed") {
      draftManualIdRef.current = result.manualId;
      setSending(false);
      setSendError(result.error);
      return;
    }
    draftManualIdRef.current = null;
    rememberCaptureOrigin(result.manualId, "screen");
    clearReviewUrls();
    navigate(`/processing/${result.manualId}`);
  }

  const recording = phase === "recording";
  const isPaused = phase === "paused";
  const isLive = recording || isPaused;
  // Keep the screen awake for the whole take (pause included — a sleeping screen kills the capture).
  useWakeLock(isLive);
  // On devices without getDisplayMedia (iOS / most Android), guide the user to the OS screen recorder.
  const sr = supported ? null : screenRecordSteps();

  return (
    <div className="cap-shell" style={{ height: "100dvh", display: "flex", flexDirection: "column" }}>
      <h1 className="sr-only">画面録画から作成</h1>
      <span className="sr-only" role="status" aria-live="polite">
        {srMsg}
      </span>
      <input ref={fileRef} type="file" accept="video/*" hidden onChange={onPickFile} />
      <div className="viewfinder" aria-hidden={review ? true : undefined}>
        <span className="vf-corner vf-tl" />
        <span className="vf-corner vf-tr" />
        <span className="vf-corner vf-bl" />
        <span className="vf-corner vf-br" />

        {/* live preview of the captured screen (only while recording) */}
        <video
          ref={previewRef}
          muted
          playsInline
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            background: "#0a0b0e",
            opacity: isLive ? 1 : 0,
            transition: "opacity .3s",
          }}
        />

        <div className="topstrip" style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 12, padding: "max(16px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) 16px max(20px, env(safe-area-inset-left))" }}>
          <Link className="btn btn--ghost" to={closeTo} aria-label="閉じる" tabIndex={review ? -1 : undefined} style={{ minHeight: 56, minWidth: 56, padding: "8px 14px", borderRadius: 12, background: "rgba(0,0,0,.4)" }}>
            <Icon name="close" size={20} />
          </Link>
          <span className="grow" />
          {isLive ? (
            <span className="timer" style={{ fontFamily: "var(--font-mono)", fontSize: 18, background: "rgba(0,0,0,.5)", padding: "6px 12px", borderRadius: 999, display: "flex", alignItems: "center", gap: 8 }}>
              {recording ? <span className="rec-dot" aria-hidden="true" /> : <Icon name="pause" size={14} aria-hidden />}
              {mmss(elapsed)}
            </span>
          ) : null}
        </div>

        <div className="guide reveal d2" style={{ position: "absolute", left: 0, right: 0, top: "44%", transform: "translateY(-50%)", textAlign: "center", padding: "0 30px", visibility: review ? "hidden" : undefined }}>
          {phase === "idle" ? (
            supported ? (
              /* ---- PC: live screen recording via getDisplayMedia ---- */
              <div style={{ position: "relative", display: "inline-block", padding: "6px 10px", pointerEvents: "auto" }}>
                <span aria-hidden="true" style={{ position: "absolute", inset: "-40% -30%", background: "radial-gradient(58% 58% at 50% 50%, rgba(8,9,12,.62), transparent 72%)", zIndex: -1 }} />
                <div className="kicker" style={{ color: "var(--hi)", letterSpacing: "0.24em" }}>PC画面を録画</div>
                <h2 className="display jp" style={{ fontSize: "clamp(24px,5.6vw,40px)", lineHeight: 1.12, letterSpacing: "-0.01em", margin: "10px 0 14px", textShadow: "0 2px 20px rgba(0,0,0,.6)" }}>
                  <span className="hl">操作しながら話す</span>、
                  <br />
                  それだけで手順に。
                </h2>
                {error ? (
                  <p className="mono" style={{ color: "var(--danger)", fontSize: 12.5, lineHeight: 1.7, margin: "0 auto 14px", maxWidth: 460 }}>
                    {error}
                  </p>
                ) : (
                  <p className="mono" style={{ color: "var(--ink)", opacity: 0.82, fontSize: 12, letterSpacing: "0.05em", margin: "0 0 16px" }}>
                    画面 / ウィンドウ / タブ を選んで録画 — マイクの実況ごと AI が手順化。
                  </p>
                )}

                <div className="row" role="group" aria-label="音声入力" style={{ gap: 8, justifyContent: "center", marginBottom: 16, flexWrap: "wrap" }}>
                  <button type="button" aria-pressed={micOn} onClick={() => setMicOn((v) => !v)} style={toggleStyle(micOn)}>
                    <Icon name="mic" size={16} /> マイク {micOn ? "ON" : "OFF"}
                  </button>
                  <button type="button" aria-pressed={sysOn} onClick={() => setSysOn((v) => !v)} style={toggleStyle(sysOn)}>
                    <Icon name="speaker" size={16} /> PC内の音 {sysOn ? "ON" : "OFF"}
                  </button>
                </div>

                <button className="btn btn--hi btn--lg" onClick={() => void start()} style={{ minWidth: 240 }}>
                  <Icon name="target" size={16} /> 画面を選んで録画開始
                </button>
                <div style={{ marginTop: 12 }}>
                  <button type="button" onClick={() => fileRef.current?.click()} style={uploadLinkStyle}>
                    <Icon name="upload" size={14} /> すでに録画した動画をアップロード
                  </button>
                </div>
              </div>
            ) : (
              /* ---- iOS / Android / unsupported: record with the OS, then upload ----
                   maxHeight+scroll: the 4-step guide is ~450px tall and the viewfinder clips
                   overflow — on short (landscape) viewports the upload button must stay reachable */
              <div style={{ position: "relative", display: "inline-block", padding: "6px 10px", pointerEvents: "auto", maxWidth: 520, maxHeight: "78dvh", overflowY: "auto" }}>
                <span aria-hidden="true" style={{ position: "absolute", inset: "-26% -20%", background: "radial-gradient(62% 62% at 50% 44%, rgba(8,9,12,.7), transparent 74%)", zIndex: -1 }} />
                <div className="kicker" style={{ color: "var(--hi)", letterSpacing: "0.22em" }}>{sr?.label}で画面収録</div>
                <h2 className="display jp" style={{ fontSize: "clamp(22px,5.2vw,34px)", lineHeight: 1.16, letterSpacing: "-0.01em", margin: "10px 0 8px", textShadow: "0 2px 20px rgba(0,0,0,.6)" }}>
                  端末の<span className="hl">画面収録</span>で撮って、
                  <br />
                  そのまま手順に。
                </h2>
                <p className="mono" style={{ color: "var(--ink)", opacity: 0.8, fontSize: 11.5, lineHeight: 1.7, letterSpacing: "0.03em", margin: "0 0 16px" }}>
                  この端末のブラウザは画面共有録画に未対応です。OS標準の画面収録で撮ってアップロードすれば、動画と同じAI処理で手順化します。
                </p>

                <ol style={{ textAlign: "left", margin: "0 auto 18px", padding: 0, listStyle: "none", display: "grid", gap: 9, maxWidth: 430 }}>
                  {sr?.steps.map((s, i) => (
                    <li key={i} style={{ display: "grid", gridTemplateColumns: "24px 1fr", gap: 11, alignItems: "start" }}>
                      <span className="mono" aria-hidden="true" style={{ fontSize: 12, fontWeight: 700, color: "var(--hi)", lineHeight: "18px", textAlign: "center", borderBottom: "2px solid color-mix(in srgb,var(--hi) 55%,transparent)" }}>
                        {i + 1}
                      </span>
                      <span className="jp" style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--ink)" }}>{s}</span>
                    </li>
                  ))}
                </ol>

                {error ? (
                  <p className="mono" style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.7, margin: "0 auto 12px", maxWidth: 440 }}>
                    {error}
                  </p>
                ) : null}

                <button className="btn btn--hi btn--lg" onClick={() => fileRef.current?.click()} style={{ minWidth: 240 }}>
                  <Icon name="upload" size={16} /> 録画した動画を選ぶ
                </button>
              </div>
            )
          ) : isPaused ? (
            <div className="mono" style={{ fontSize: 13, letterSpacing: "0.2em", color: "var(--ink)", textShadow: "0 2px 14px rgba(0,0,0,.65)" }}>
              <Icon name="pause" size={14} aria-hidden /> 一時停止中 — 中央で<span className="hl">再開</span>、右で完了
            </div>
          ) : (
            <div className="mono" style={{ fontSize: 13, letterSpacing: "0.2em", color: "var(--ink)", textShadow: "0 2px 14px rgba(0,0,0,.65)" }}>
              <span className="rec-dot" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8 }} />
              {phase === "saving" ? "保存中…" : "画面を録画中 — 区切りで一時停止OK、実況しながら操作してください"}
            </div>
          )}
        </div>
      </div>

      {supported && !review ? (
        <div
          className="controls"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "22px 24px calc(26px + env(safe-area-inset-bottom))",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: 16,
            background: "linear-gradient(0deg,rgba(0,0,0,.7),transparent)",
          }}
        >
          <div className="narr" style={narrStyle}>
            <div className="wave" aria-hidden="true" style={{ opacity: recording ? 1 : 0.35 }}>
              {Array.from({ length: 9 }, (_, i) => (
                <i key={i} />
              ))}
            </div>
            <span className="mono" style={{ fontSize: 12, color: isPaused ? "var(--muted)" : "var(--hi)" }}>
              {recording ? "録音中" : isPaused ? "一時停止" : phase === "saving" ? "保存中" : "待機中"}
            </span>
          </div>
          <button
            className={`shutter${recording ? " shutter--rec" : ""}${isPaused ? " shutter--paused" : ""}`}
            aria-label={phase === "idle" ? "録画開始" : recording ? "一時停止" : isPaused ? "録画を再開" : "保存中"}
            aria-pressed={isLive}
            disabled={phase === "saving"}
            onClick={() => {
              if (recording) pause();
              else if (isPaused) resume();
              else if (phase === "idle") void start();
            }}
          />
          <div style={{ justifySelf: "end" }}>
            <Btn
              variant="hi"
              lg
              icon={<Icon name="check" size={18} />}
              onClick={stop}
              aria-label="録画を完了"
              style={{ visibility: isLive ? "visible" : "hidden" }}
            >
              完了
            </Btn>
          </div>
        </div>
      ) : null}

      {review ? (
        <ReviewGate
          url={review.url}
          posterUrl={posterUrl}
          playable={review.playable}
          onUnplayable={() => setReview((r) => (r ? { ...r, playable: false } : r))}
          durationMs={review.durationMs}
          sending={sending}
          progress={sendProgress}
          error={sendError}
          onSend={() => void sendReview()}
          onRetake={retake}
          onDiscard={discard}
          retakeLabel="録画し直す"
        />
      ) : null}
    </div>
  );
}

/** OS-specific guidance for recording the screen when the browser lacks getDisplayMedia (mobile). */
function screenRecordSteps(): { label: string; steps: string[] } {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIPadOS = /Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document;
  if (/iP(hone|ad|od)/.test(ua) || isIPadOS) {
    return {
      label: "iPhone / iPad",
      steps: [
        "コントロールセンターを開き ◉「画面収録」をタップ（声も残すなら長押し → マイクをオン）",
        "記録したい画面・アプリ操作を、声で実況しながら進める",
        "ステータスバーの赤い表示をタップして収録を停止",
        "下のボタンから、いま録画した動画を選んでアップロード",
      ],
    };
  }
  if (/Android/.test(ua)) {
    return {
      label: "Android",
      steps: [
        "クイック設定パネルの「スクリーンレコード（画面録画）」を開始（マイクをオンに）",
        "記録したい操作を、声で実況しながら進める",
        "通知パネルから録画を停止",
        "下のボタンから、いま録画した動画を選んでアップロード",
      ],
    };
  }
  return {
    label: "この端末",
    steps: [
      "端末の画面録画機能で、マイク（実況）をオンにして録画",
      "記録したい操作を声で説明しながら進める",
      "録画を停止",
      "下のボタンから録画した動画を選んでアップロード",
    ],
  };
}

const narrStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  background: "rgba(0,0,0,.42)",
  padding: "10px 14px",
  borderRadius: 14,
  backdropFilter: "blur(8px)",
};

const uploadLinkStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "none",
  border: "none",
  color: "var(--muted)",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  letterSpacing: "0.04em",
  textDecoration: "underline",
  textUnderlineOffset: 3,
  cursor: "pointer",
};

function toggleStyle(on: boolean): CSSProperties {
  return {
    padding: "9px 15px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "var(--font-mono)",
    letterSpacing: "0.04em",
    border: on ? "1px solid var(--hi)" : "1px solid var(--line-2)",
    background: on ? "color-mix(in srgb,var(--hi) 16%,rgba(0,0,0,.4))" : "rgba(0,0,0,.4)",
    color: on ? "var(--hi)" : "var(--muted)",
    cursor: "pointer",
  };
}
